import React from "react"
import { makeStyles } from "@material-ui/core/styles"
import Typography from "@material-ui/core/Typography"
import { Link, injectIntl } from "gatsby-plugin-intl"
import { useStaticQuery, graphql } from "gatsby"
import Img from "gatsby-image"

// Quiet surgeon-attribution credit for treatment & guide pages. Renders through
// MUI <Typography> so it inherits the site's theme font, and uses the site's own
// #828282 label colour — no borders, backgrounds or rules of its own, so it reads
// as part of the surrounding content. Alignment/width come from the column the
// page places it in (mirror the section above it). Names WHO provides the care and
// links to their credentials; the machine-readable half (BIG number, memberships,
// lastReviewed date) lives in the MedicalWebPage / Physician JSON-LD from <SEO>.
//
// Props:
//   surgeon: "callum" | "susan"     (defaults to callum)
//   variant: "surgeon" | "reviewed" (service page vs long-form guide/article)
//   updated: "YYYY-MM-DD"           (only shown in the "reviewed" variant)
//   photo:   true                   (adds the /about portrait beside the text)

const SURGEONS = {
  callum: {
    name: { en: "Dr Callum Faris", nl: "Dr Callum Faris" },
    anchor: "callum",
    // Person-specific eyebrow: Callum is a surgeon, Susan is not.
    eyebrow: { en: "Your surgeon", nl: "Uw chirurg" },
    // Role and portrait are pulled from /about so the two pages can't drift apart.
    roleId: "about.callum.title",
    photoKey: "callum",
    // Both portraits frame the head high, so the square crop is pulled upwards.
    focus: "50% 18%",
  },
  susan: {
    // NL uses the doctorandus title: she is "Drs." in Dutch, "Dr" in English.
    name: { en: "Dr Susan Schmidt", nl: "Drs. Susan Schmidt" },
    anchor: "susan",
    eyebrow: { en: "Your aesthetic doctor", nl: "Uw cosmetisch arts" },
    roleId: "about.susan.title",
    photoKey: "susan",
    focus: "50% 16%",
  },
}

const LABELS = {
  en: {
    reviewed: "Written & medically reviewed by",
    updated: "Last reviewed",
    profile: "View profile",
  },
  nl: {
    reviewed: "Geschreven en medisch beoordeeld door",
    updated: "Laatst beoordeeld",
    profile: "Bekijk profiel",
  },
}

const useStyles = makeStyles((theme) => ({
  wrap: {
    maxWidth: 350,
    textAlign: "left",
    // Own vertical breathing room so it doesn't jam against the next section.
    margin: "12px 0 48px",
  },
  // Photo variant: portrait leads, text sits to its right. Tighter bottom margin
  // so two stacked bylines read as one pair, not two sections.
  wrapWithPhoto: {
    display: "flex",
    alignItems: "center",
    maxWidth: 450,
    margin: "12px 0 32px",
    [theme.breakpoints.down("xs")]: {
      // The column this sits in has no gutter of its own on mobile, which would
      // push the byline flush against the screen edge. Match the page's own.
      boxSizing: "border-box",
      maxWidth: "100%",
      padding: "0 24px",
    },
  },
  text: {
    flex: 1,
    minWidth: 0,
  },
  photo: {
    flex: "0 0 auto",
    // Plain rectangle at the same portrait ratio /about crops the doctors to
    // (351x500) — no radius or ring, matching how the site frames them there.
    // Bigger on desktop, where the card sits in its own right-hand column.
    width: 96,
    height: 137,
    marginRight: 22,
    [theme.breakpoints.up("md")]: {
      width: 124,
      height: 177,
      marginRight: 26,
    },
  },
  eyebrow: {
    color: "#828282",
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    fontSize: "0.82rem",
    fontWeight: 500,
    lineHeight: 1.3,
    [theme.breakpoints.down("sm")]: {
      lineHeight: 1.2,
    },
  },
  name: {
    color: "white",
    // 400/1.1rem matches the page's body copy. At 300 the browser picks
    // Helvetica Neue *Light*, which reads as a different, too-thin typeface.
    fontWeight: 400,
    fontSize: "1.1rem",
    marginTop: 6,
    lineHeight: 1.4,
    // Below md the portrait is only 137px tall; Dr Faris's long role otherwise
    // wraps to 5 lines and the text block overhangs the photo top and bottom.
    // A hair smaller with tighter leading drops it to 4 lines that fit the frame.
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.05rem",
      lineHeight: 1.25,
    },
  },
  who: { fontWeight: 600 },
  role: { color: "#c6c1bb" },
  link: {
    display: "inline-block",
    marginTop: 8,
    color: "#C8AA78",
    fontSize: "0.9rem",
    letterSpacing: "0.03em",
    textDecoration: "none",
    "&:hover": { textDecoration: "underline" },
    "&:focus-visible": { outline: "2px solid #C8AA78", outlineOffset: 3 },
  },
  updated: { color: "#8f8f8f", fontSize: "0.82rem", marginTop: 8 },
}))

const SurgeonByline = ({
  intl,
  surgeon = "callum",
  variant = "surgeon",
  updated,
  photo = false,
}) => {
  const classes = useStyles()
  const locale = intl.locale === "en" ? "en" : "nl"
  const person = SURGEONS[surgeon] || SURGEONS.callum
  const L = LABELS[locale]
  const role = intl.formatMessage({ id: person.roleId })

  const portraits = useStaticQuery(graphql`
    query SurgeonBylinePortraits {
      callum: file(relativePath: { eq: "callum-faris-portrait.webp" }) {
        childImageSharp {
          fluid(maxWidth: 300, quality: 90) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      susan: file(relativePath: { eq: "susan-schmidt-portrait.webp" }) {
        childImageSharp {
          fluid(maxWidth: 300, quality: 90) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
    }
  `)

  let updatedLabel = null
  if (variant === "reviewed" && updated) {
    const d = new Date(updated)
    if (!isNaN(d.getTime())) {
      updatedLabel = d.toLocaleDateString(locale === "en" ? "en-GB" : "nl-NL", {
        month: "long",
        year: "numeric",
      })
    }
  }

  const body = (
    <>
      <Typography component="p" className={classes.eyebrow}>
        {variant === "reviewed" ? L.reviewed : person.eyebrow[locale]}
      </Typography>
      <Typography component="p" className={classes.name}>
        <span className={classes.who}>{person.name[locale]}</span>{" "}
        <span className={classes.role}>— {role}</span>
      </Typography>
      <Link to={`/about#${person.anchor}`} className={classes.link}>
        {L.profile} →
      </Link>
      {updatedLabel && (
        <Typography component="p" className={classes.updated}>
          {L.updated}: {updatedLabel}
        </Typography>
      )}
    </>
  )

  if (!photo) return <div className={classes.wrap}>{body}</div>

  return (
    <div className={`${classes.wrap} ${classes.wrapWithPhoto}`}>
      <Img
        fluid={portraits[person.photoKey].childImageSharp.fluid}
        className={classes.photo}
        imgStyle={{ objectFit: "cover", objectPosition: person.focus }}
        alt={person.name[locale]}
      />
      <div className={classes.text}>{body}</div>
    </div>
  )
}

export default injectIntl(SurgeonByline)
